'use client'

import Script from 'next/script'
import { usePathname, useSearchParams } from 'next/navigation'
import { useEffect, Suspense } from 'react'

const GA_MEASUREMENT_ID = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID || 'G-ZBQ4DYREKZ'

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void
  }
}

/**
 * Map pathname to a human-readable page title for analytics.
 * This ensures each page has a meaningful name in analytics reports
 * regardless of the document.title value.
 */
function getPageTitle(pathname: string): string {
  const pageTitles: Record<string, string> = {
    '/': 'Home',
    '/demo': 'Demo',
  }

  return pageTitles[pathname] || pathname
}

function AnalyticsPageTracker() {
  const pathname = usePathname()
  const searchParams = useSearchParams()

  useEffect(() => {
    if (pathname && window.gtag) {
      const url = pathname + (searchParams?.toString() ? `?${searchParams.toString()}` : '')
      window.gtag('config', GA_MEASUREMENT_ID, {
        page_path: url,
        page_title: getPageTitle(pathname),
      })
    }
  }, [pathname, searchParams])

  return null
}

export default function Analytics() {
  return (
    <>
      {/* Google Analytics */}
      <Script
        src={`https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`}
        strategy="afterInteractive"
      />
      <Script id="google-analytics" strategy="afterInteractive">
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', '${GA_MEASUREMENT_ID}', {
            page_path: window.location.pathname,
          });
        `}
      </Script>
      <Suspense fallback={null}>
        <AnalyticsPageTracker />
      </Suspense>
    </>
  )
}
